import { ButtonHTMLAttributes, useEffect, useState } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import loaderLight from '../../../assets/loaderGif.gif';
import loaderDark from '../../../assets/icons/loaderDark.gif';
import { Link } from 'react-router-dom';

const buttonStyles = cva(
  [
    // Base styles
    'inline-flex min-w-fit items-center justify-center gap-2',
    'font-medium text-sm leading-5',
    'rounded-[18px]',
    'transition-all duration-200 ease-in-out',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    'select-none',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-blue-700 text-white',
          'hover:bg-blue-800',
          'focus-visible:ring-blue-500',
          'active:bg-blue-800',
          'disabled:bg-grey-100 disabled:text-grey-600',
        ],
        outline:
          'border border-taupe-600 bg-white transition-shadow active:inset-shadow-green-900 text-grey-800 disabled:border-black-200 disabled:text-black-300 hover:bg-gray-50 hover:text-black-900 active:border-black-200 active:bg-white active:shadow-[0px_0px_0px_4px_#F2F4F7]',
        ghost: '!p-2 !px-[10px] hover:bg-black-100 active:bg-black-200',
        link: 'text-blue-700 hover:underline',
      },
      size: {
        large: 'w-full !text-base font-semibold', // legacy
        sm: 'px-3 py-2 text-sm',
        md: 'px-4 py-2.5 text-sm',
        lg: 'px-6 py-3 text-base',
        xl: 'px-8 py-4 text-lg',
        '2xl': 'px-10 py-5 text-xl',
      },
      isLoading: {
        true: '',
      },
      disabled: {
        true: '!cursor-not-allowed',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonStyles> {
  isSubmit?: boolean;
  isLoading?: boolean;
  className?: string;
  disabled?: boolean;
  asLink?: boolean;
  to?: string;

  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

function Button({
  children,
  isSubmit,
  disabled = false,
  isLoading = false,
  className,
  variant = 'primary',
  size,
  asLink = false,
  to,
  leftIcon,
  rightIcon,
  ...rest
}: ButtonProps) {
  const [showLoaderMargin, setShowLoaderMargin] = useState(false);

  useEffect(() => {
    if (!isLoading) {
      setShowLoaderMargin(false);
    }
  }, [isLoading]);

  useEffect(() => {
    if (isLoading) {
      const timeoutId = setTimeout(() => {
        setShowLoaderMargin(true);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isLoading]);

  const renderContent = () => {
    if (isLoading && !disabled) {
      return (
        <>
          {isLoading && !disabled && (
            <img
              src={variant === 'outline' ? loaderDark : loaderLight}
              alt="Loading"
              className="mr-2 w-[20px]"
              style={{
                transition: 'margin-right 0.3s ease',
                marginRight: showLoaderMargin ? '0.5rem' : '0',
              }}
            />
          )}
          {children}
        </>
      );
    }

    return (
      <>
        {leftIcon && (
          <span className={`mr-1 ${disabled ? 'opacity-50' : ''}`}>
            {leftIcon}
          </span>
        )}
        {children}
        {rightIcon && (
          <span className={`ml-1 ${disabled ? 'opacity-50' : ''}`}>
            {rightIcon}
          </span>
        )}
      </>
    );
  };

  const classes = twMerge(
    buttonStyles({ variant, size, isLoading, disabled }),
    className,
  );

  if (asLink && to) {
    return (
      <Link to={to} className={classes}>
        {renderContent()}
      </Link>
    );
  }
  return (
    <button
      type={rest.type ?? (isSubmit ? 'submit' : 'button')}
      disabled={isLoading || disabled}
      className={twMerge(
        buttonStyles({ variant, size, isLoading, disabled }),
        className,
      )}
      {...rest}
    >
      {renderContent()}
    </button>
  );
}

export default Button;
